import { HardwareService } from "../HardwareService";
import {
  PrintApiJob,
  PrintApiJobStatus,
  PrintApiImplementationType,
  PrintApiPrinter,
  PrintApiPrinterCreateParams,
  PrintApiSyncResult,
} from "./print-api/types";
import { isPrintApiPeripheral } from "./print-api/isPrintApiPeripheral";
import { printerMatchers } from "./print-api/PrintApiPrinterMatcher";
import { PrintApiClient } from "./print-api/PrintApiClient";
import { PrintApiService } from "./print-api/PrintApiService";
import { LabelPrinter } from "../label-printer/LabelPrinter";
import { ReceiptPrinter } from "../receipt-printer/ReceiptPrinter";
import { Peripheral } from "./Peripheral";
import { PeripheralService } from "./PeripheralService";
import { PeripheralServiceManager } from "./PeripheralServiceManager";
import { PeripheralType, PrintApiJobResult } from "./types";

const cloudImplementationTypes: PrintApiImplementationType[] = [
  PrintApiImplementationType.CloudPRNT,
  PrintApiImplementationType.DesktopAgent,
  PrintApiImplementationType.PrintNode,
  PrintApiImplementationType.ServerDirect,
  PrintApiImplementationType.Weblink,
];

const localImplementationTypes: PrintApiImplementationType[] = [
  PrintApiImplementationType.WebUSB,
  PrintApiImplementationType.WebviewApp,
  PrintApiImplementationType.Native,
];

export class PeripheralServicePrinterManager<
  T extends Peripheral
> extends PeripheralServiceManager<T> {
  constructor(type: PeripheralType) {
    super(type);
  }

  // Canonical keys of local devices for which sync has been attempted.
  // Persisted to sessionStorage (localStorage fallback) so page reloads don't re-sync the same printers.
  // Scoped to location + peripheral type to prevent cross-location false-positives.
  private readonly syncedPrinterKeys = new Set<string>();

  // Maps PrintApi device ID → local implementation device ID for USB printers.
  // Rebuilt on every device update so resolvePrinterById resolves cross-impl matches in O(1).
  private readonly printApiToLocalMap = new Map<string, string>();

  // True once the registered PrintApiService has successfully fetched its printer list.
  // Derived from the service instance — no duplicate manager-level gate needed.
  private get printApiReady(): boolean {
    return this.state.some(
      (s) =>
        s.enabled &&
        s.service instanceof PrintApiService &&
        s.service.discoveryComplete
    );
  }

  private get syncStorageKey(): string {
    const lid = HardwareService.printApiClient?.locationIdentifier ?? "unknown";
    return `printapi:synced:${lid}:${this.type}`;
  }

  private getStorage(): Storage {
    return typeof sessionStorage !== "undefined"
      ? sessionStorage
      : localStorage;
  }

  private loadSyncedKeysFromStorage(): void {
    try {
      const raw = this.getStorage().getItem(this.syncStorageKey);
      if (raw)
        (JSON.parse(raw) as string[]).forEach((k) =>
          this.syncedPrinterKeys.add(k)
        );
    } catch {
      /* corrupted — start fresh */
    }
  }

  private persistSyncedKeys(): void {
    try {
      this.getStorage().setItem(
        this.syncStorageKey,
        JSON.stringify([...this.syncedPrinterKeys])
      );
    } catch {
      /* storage full — in-memory dedup still works this session */
    }
  }

  private rebuildPrintApiMap(): void {
    this.printApiToLocalMap.clear();
    const allDevices = super.devices;
    const localDevices = allDevices.filter((d) => !isPrintApiPeripheral(d));

    for (const device of allDevices) {
      if (!isPrintApiPeripheral(device)) continue;
      if (!localImplementationTypes.includes(device.device.implementationType))
        continue;

      const matcher = printerMatchers.find(
        (m) => m.implementationType === device.device.implementationType
      );

      // Exact match via the registered matcher (e.g. IP/MAC for WebviewApp, vendorId+productId for WebUSB).
      let match = matcher
        ? localDevices.find((local) => matcher.matches(local, device.device))
        : undefined;

      // Fall back to vendorId+productId for devices without a matcher or when the matcher finds nothing.
      if (!match) {
        const { vendorId, productId } = device.device.metadata;
        if (vendorId && productId) {
          match = localDevices.find((local) => {
            return local.metadata?.vendorId === vendorId && local.metadata?.productId === productId;
          });
        }
      }

      if (match) this.printApiToLocalMap.set(device.id, match.id);
    }
  }

  // All devices in the grid regardless of mode — used for reconciliation
  get allDevices(): T[] {
    return super.devices;
  }

  // Returns the best locally-available device for the given ID when PrintApi is active.
  // Uses the matcher-built printApiToLocalMap for exact resolution (IP/MAC for LAN, vendorId+productId
  // for USB). Falls back to the PrintApi wrapper if no local match exists, or to super.deviceById
  // for non-PrintApi IDs. Backward-compatible: behaves identically when printApiClient is unset.
  resolvePrinterById(id: string | undefined): T | undefined {
    if (!id) return undefined;
    const device = super.deviceById(id);
    if (!HardwareService.printApiClient || !device) return device;
    if (!isPrintApiPeripheral(device)) return device;
    if (!localImplementationTypes.includes(device.device.implementationType))
      return device;

    const localId = this.printApiToLocalMap.get(id);
    if (!localId) return device;

    return super.deviceById(localId) ?? device;
  }

  // Whether a PrintApi service is registered on THIS manager (label or receipt independently).
  // HardwareService.printApiClient is shared across both peripheral types, so a rollout that only
  // enables PrintApi for one type (e.g. receipts) must not cause the other type's manager to switch
  // into PrintApi-only filtering below and hide its still-active local devices (e.g. PrintNode labels).
  private get hasPrintApiService(): boolean {
    return this.state.some((s) => s.enabled && s.service instanceof PrintApiService);
  }

  // Only expose PrintApi devices; local types (WebUSB, etc.) require their backing service to be registered.
  override get devices(): T[] {
    if (!HardwareService.printApiClient || !this.hasPrintApiService) return super.devices;

    const enabled = new Set(
      this.state.filter((s) => s.enabled).map((s) => s.service.name)
    );

    return super.devices.filter((device) => {
      if (!isPrintApiPeripheral(device)) return false;
      if (cloudImplementationTypes.includes(device.device.implementationType))
        return true;
      const matcher = printerMatchers.find(
        (m) => m.implementationType === device.device.implementationType
      );
      return matcher?.serviceNames.some((name) => enabled.has(name)) ?? false;
    });
  }

  // Scan all devices in the grid (including shadow services) for a local implementation
  // device that corresponds to the given PrintApiPrinter record.
  private findLocalDevice(printApiPrinter: PrintApiPrinter): T | undefined {
    const matcher = printerMatchers.find(
      (m) => m.implementationType === printApiPrinter.implementationType
    );
    if (!matcher) return undefined;

    return super.devices.find(
      (device) =>
        !isPrintApiPeripheral(device) &&
        matcher.matches(device, printApiPrinter)
    );
  }

  protected override updateServiceResultsAndEmitEvent(
    devices: T[],
    service: PeripheralService<T>,
    remove = true
  ): void {
    super.updateServiceResultsAndEmitEvent(devices, service, remove);
    this.rebuildPrintApiMap();

    const client = HardwareService.printApiClient;
    if (!client || !this.printApiReady) return;

    void this.syncMissingLocalDevices(client);
  }

  private async syncMissingLocalDevices(client: PrintApiClient): Promise<void> {
    // Load persisted keys so page reloads don't re-sync already-known devices.
    // Idempotent — adding already-present keys to the Set is a no-op.
    this.loadSyncedKeysFromStorage();

    const toSync: Array<{ params: PrintApiPrinterCreateParams }> = [];

    for (const device of super.devices) {
      const serviceName = this.serviceNameById(device.id);
      if (!serviceName) continue;

      const matcher = printerMatchers.find((m) =>
        (m.serviceNames as readonly string[]).includes(serviceName)
      );
      if (!matcher) continue;

      const params = matcher.buildCreateParams(device);
      if (!params) continue;

      const key = matcher.buildSyncKey(params);
      if (this.syncedPrinterKeys.has(key)) continue;

      // Claim the key before any await to block concurrent re-entry.
      this.syncedPrinterKeys.add(key);
      toSync.push({ params });
    }

    if (!toSync.length) return;

    await this.syncDevices(toSync, client);
  }

  private async syncDevices(
    items: Array<{ params: PrintApiPrinterCreateParams }>,
    client: PrintApiClient
  ): Promise<void> {
    let result: PrintApiSyncResult;
    try {
      result = await client.syncPrinters({
        printers: items.map((i) => i.params),
      });
    } catch (e) {
      HardwareService.logger.e(
        "Failed to sync local printers to print repo",
        e
      );
      // Release keys so devices can retry on next discovery cycle or page reload.
      items.forEach((i) => {
        const m = printerMatchers.find(
          (m) => m.implementationType === i.params.implementationType
        );
        if (m) this.syncedPrinterKeys.delete(m.buildSyncKey(i.params));
      });
      return;
    }

    HardwareService.logger.d("Printer sync result", {
      created: result.created.length,
      updated: result.updated.length,
      errored: result.errored.length,
    });

    // Persist keys after a successful call — page reloads will skip these devices.
    this.persistSyncedKeys();

    // Invalidate the PrintAPI service if any printers were created or errored.
    // created: new record exists — re-fetch so it appears in the device grid.
    // errored: likely a duplicate from another client that already created it —
    //          re-fetching picks up that record and associates it locally.
    const shouldInvalidate =
      result.created.length > 0 || result.errored.length > 0;
    if (shouldInvalidate) {
      const printApiService = this.state.find(
        (s) => s.enabled && s.service instanceof PrintApiService
      )?.service;
      if (printApiService) {
        await this.invalidate(printApiService.name);
      }
    }
  }

  override unregister(...services: PeripheralService<T>[]): void {
    // Capture storage key BEFORE super.unregister disposes the service instance.
    // By the time unregister is called, HardwareService.printApiClient is already null
    // (setPrintApiConfig clears printApiConfig before calling unregisterServiceName),
    // so syncStorageKey would resolve to 'unknown'. Get it from the service directly.
    const printApiService = services.find(
      (s) => s instanceof PrintApiService
    ) as PrintApiService<any> | undefined;
    const storageKey = printApiService
      ? `printapi:synced:${printApiService.locationIdentifier}:${this.type}`
      : undefined;

    super.unregister(...services);
    this.rebuildPrintApiMap();

    if (storageKey) {
      this.syncedPrinterKeys.clear();
      try {
        this.getStorage().removeItem(storageKey);
      } catch {
        /* ignore */
      }
      // printApiReady automatically becomes false — the disposed service instance
      // (with discoveryComplete = true) is gone; re-registration creates a fresh one.
    }
  }

  // Only used when print worker is enabled to consistently update job status through worker api
  async print(job: PrintApiJob): Promise<PrintApiJobResult> {
    var success = false;
    var error: string | undefined;

    var client: PrintApiClient | undefined;

    try {
      client = HardwareService.printApiClient;
      if (!client) {
        throw new Error("Print API client not available");
      }

      const printer = this.resolvePrinterById(`print-api-${job.printerId}`);
      if (!printer) {
        throw new Error(`Printer ${job.printerId} not found`);
      }

      if (
        isPrintApiPeripheral(printer) &&
        cloudImplementationTypes.includes(printer.device.implementationType)
      ) {
        HardwareService.logger.warn({
          message: `print() called for cloud-managed printer (${printer.device.implementationType}). Job should be handled by the cloud service.`,
        });
        return {
          success: false,
          error: `print() should not be called for ${printer.device.implementationType} printers`,
        };
      }

      if (
        printer instanceof ReceiptPrinter ||
        printer instanceof LabelPrinter
      ) {
        if (!job.base64Content) {
          throw new Error("Print content is missing");
        }

        if (job.status !== PrintApiJobStatus.completed) {
          // TODO: consider consolidating multiple status updates into a single call
          void HardwareService.printApiClient
            ?.updateJobStatus(job, {
              error,
              status: PrintApiJobStatus.processing,
            })
            .catch((e) =>
              HardwareService.logger.e(
                "Failed to update job status to processing",
                e
              )
            );
        }

        const bytes = Uint8Array.from(atob(job.base64Content), (c) =>
          c.charCodeAt(0)
        );

        let printTarget: ReceiptPrinter | LabelPrinter | undefined;
        if (!isPrintApiPeripheral(printer)) {
          // resolvePrinterById already resolved to the local implementation device
          printTarget = printer;
        } else if (
          localImplementationTypes.includes(printer.device.implementationType)
        ) {
          const localDevice = this.findLocalDevice(printer.device);
          if (!localDevice) {
            throw new Error(
              `Local device for printer ${printer.name} not connected`
            );
          }
          if (
            localDevice instanceof ReceiptPrinter ||
            localDevice instanceof LabelPrinter
          ) {
            printTarget = localDevice;
          }
        }

        if (printTarget instanceof ReceiptPrinter) {
          success = await printTarget.print(bytes);
        } else if (printTarget instanceof LabelPrinter) {
          success = await printTarget.print(bytes);
        }

        if (success) {
          job.status = PrintApiJobStatus.completed;
        }
      }
    } catch (e) {
      error = e instanceof Error ? e.message : "Unknown error";
    }

    // report to print api
    if (!success && !error) {
      error = "Unknown print failure";
    }

    if (client) {
      void client
        .updateJobStatus(job, { error, status: PrintApiJobStatus.completed })
        .catch((e) =>
          HardwareService.logger.e(
            "Failed to update job status to completed",
            e
          )
        );
    }

    return { success, error };
  }
}
